// Generic segmented status bar — configurable via statuses + counts props
// Used by startup form (field statuses) and applications (application statuses)
import { useTranslation } from 'react-i18next';

export interface StatusConfig {
  value: string;
  color: string; // Tailwind bg class, e.g. 'bg-[#42BEFC]'
  labelKey: string; // i18n key passed to t() in 'common' namespace
}

export interface StatusBarProps {
  statuses: StatusConfig[];
  counts: Record<string, number>;
  // i18n key for the section title (common namespace) — omit or leave empty when barOnly
  titleKey?: string;
  // When true, renders without the card wrapper (for embedding inside a parent card)
  embedded?: boolean;
  // When true, renders only the segmented bar — no title, no legend
  barOnly?: boolean;
}

export function StatusBar({
  statuses,
  counts,
  titleKey = '',
  embedded = false,
  barOnly = false,
}: StatusBarProps) {
  const { t } = useTranslation('common');
  const total = statuses.reduce((sum, { value }) => sum + (counts[value] ?? 0), 0);

  if (total === 0) return null;

  // Segmented bar — each segment proportional to its count
  const bar = (
    <div className="flex h-1 rounded-full overflow-hidden bg-white/5 gap-px">
      {statuses.map(({ value, color }) => {
        const pct = ((counts[value] ?? 0) / total) * 100;
        if (pct === 0) return null;
        return (
          <div
            key={value}
            className={`${color} h-full transition-all duration-500`}
            style={{ width: `${pct}%` }}
          />
        );
      })}
    </div>
  );

  if (barOnly) return bar;

  // Full view: title + bar + legend
  const content = (
    <div className="space-y-3 border-white/5 rounded-lg">
      <h3 className="text-lg font-medium text-white">{t(titleKey)}</h3>

      {bar}

      {/* Legend: colored dot + count + label */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {statuses.map(({ value, color, labelKey }) => (
          <div key={value} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-full ${color} shrink-0`} />
            <span className="text-xs text-white leading-tight">
              <span className="font-medium">{counts[value] ?? 0}</span> {t(labelKey)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  if (embedded) return content;
  return <div className="bg-[#1E1E1E] border border-white/5 rounded-xl px-5 py-4">{content}</div>;
}
